import React, { useState, useEffect, useCallback } from 'react';
import { usePortalAuth } from './PortalAuthContext';
import {
  portalDataApi,
  formatTimeRange,
  type PortalDeclaration,
  type PortalReservation,
} from './PortalApiClient';
import { PortalDeclareSheet } from './PortalDeclareSheet';

export function PortalHome() {
  const { user, logout } = usePortalAuth();
  const [weekOffset, setWeekOffset] = useState(0);
  const [declarations, setDeclarations] = useState<PortalDeclaration[]>([]);
  const [shifts, setReservations] = useState<PortalReservation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeDate, setActiveDate] = useState<string | null>(null); // ISO date of open sheet

  const days = computeWeekDays(weekOffset);
  const weekParam = isoDate(days[0]);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [avail, sh] = await Promise.all([
        portalDataApi.getAvailability(weekParam),
        portalDataApi.getReservations(weekParam),
      ]);
      setDeclarations(avail.declarations);
      setReservations(sh.reservations);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [weekParam]);

  useEffect(() => { refresh(); }, [refresh]);

  if (!user) return null;

  const propertyName = user.assignedProperty?.name || 'Availability';
  const weekLabel = formatWeekRange(days);

  const declByDate = new Map<string, PortalDeclaration>();
  for (const d of declarations) declByDate.set(d.date, d);

  const activeDayDecl = activeDate ? declByDate.get(activeDate) || null : null;
  const activeDateObj = activeDate ? days.find(d => isoDate(d) === activeDate) || null : null;
  const activeReservationsForDay = activeDate ? shifts.filter(s => s.date === activeDate) : [];

  return (
    <div className="portal-app">
      <header className="portal-header">
        <div className="portal-header-inner">
          <div className="portal-property-label">{propertyName}</div>
          <div className="portal-title-row">
            <div className="portal-title">Availability</div>
            <button
              type="button"
              onClick={() => logout()}
              className="portal-button-text"
              aria-label="Sign out"
            >Sign out</button>
          </div>
          <div className="portal-greeting">
            Hi, {user.workingName.split(' ')[0]} 👋
          </div>
        </div>
      </header>

      <main className="portal-main">
        <div className="portal-week-nav">
          <button
            type="button"
            onClick={() => setWeekOffset(w => w - 1)}
            className="portal-week-nav-button"
            aria-label="Previous week"
          >‹</button>
          <div className="portal-week-label">{weekLabel}</div>
          <button
            type="button"
            onClick={() => setWeekOffset(w => w + 1)}
            className="portal-week-nav-button"
            aria-label="Next week"
          >›</button>
        </div>

        {error && <div className="portal-error">{error}</div>}

        {loading && declarations.length === 0 && (
          <div className="portal-coming-soon">Loading…</div>
        )}

        <div className="portal-day-list">
          {days.map(d => {
            const dateStr = isoDate(d);
            const decl = declByDate.get(dateStr) || null;
            return (
              <DayCard
                key={dateStr}
                date={d}
                declaration={decl}
                onClick={() => setActiveDate(dateStr)}
              />
            );
          })}
        </div>
      </main>

      {activeDate && activeDateObj && (
        <PortalDeclareSheet
          isoDate={activeDate}
          dateLabel={formatFullDateLabel(activeDateObj)}
          existing={activeDayDecl}
          reservationsForDay={activeReservationsForDay}
          onClose={() => setActiveDate(null)}
          onChanged={() => refresh()}
        />
      )}
    </div>
  );
}

interface DayCardProps {
  date: Date;
  declaration: PortalDeclaration | null;
  onClick: () => void;
}
function DayCard({ date, declaration, onClick }: DayCardProps) {
  const isToday = isSameDay(date, new Date());
  const cls = 'portal-day-card' + (isToday ? ' is-today' : '');
  const summary = renderDeclarationSummary(declaration);
  return (
    <button type="button" className={cls} onClick={onClick}>
      <div className="portal-day-card-left">
        <div className="portal-day-name">{formatDayName(date)}</div>
        <div className="portal-day-date">{formatDayDate(date)}</div>
      </div>
      <span className={summary.cls}>{summary.text}</span>
    </button>
  );
}

/**
 * Human labels for the declaration modes, used only by the fallback below.
 *
 * Every mode has a richer branch further down that renders a time range, so
 * this is reached only when a declaration is missing the slot or times it
 * should have. Without it that path rendered the raw enum, which is how a
 * provider could end up reading the raw enum on her own portal. That value is
 * now RESERVED rather than SHIFT, so the leak would no longer be employment
 * language — but a screen showing a database constant to a provider is still
 * wrong, and this is what stops it.
 */
const MODE_LABELS: Record<string, string> = {
  RESERVED: 'Reservation',
  CUSTOM: 'Custom hours',
  ON_CALL: 'On call',
  UNAVAILABLE: 'Unavailable',
};

interface SummaryDisplay { text: string; cls: string; }
function renderDeclarationSummary(d: PortalDeclaration | null): SummaryDisplay {
  if (!d) return { text: 'Not declared', cls: 'portal-day-hint' };
  if (d.status === 'CANCELLED') {
    return { text: 'Cancelled', cls: 'portal-day-status portal-day-status-cancelled' };
  }
  // Must come before the mode branches below: without it a called-off SHIFT
  // falls through to the confirmed styling and she reads her portal as still
  // being rostered on a shift she pulled out of.
  if (d.status === 'CALLED_OFF') {
    return { text: 'Called off', cls: 'portal-day-status portal-day-status-cancelled' };
  }
  if (d.mode === 'RESERVED' && d.reservation) {
    const range = formatTimeRange(d.reservation.startTime, d.reservation.endTime);
    if (d.status === 'PENDING_OVERFLOW') {
      return { text: `${range} · Pending`, cls: 'portal-day-status portal-day-status-pending' };
    }
    return { text: range, cls: 'portal-day-status portal-day-status-confirmed' };
  }
  if (d.mode === 'CUSTOM' && d.customStart && d.customEnd) {
    const range = formatTimeRange(d.customStart, d.customEnd);
    if (d.status === 'PENDING_APPROVAL') {
      return { text: `${range} · Pending`, cls: 'portal-day-status portal-day-status-pending' };
    }
    if (d.status === 'CONFIRMED') {
      return { text: range, cls: 'portal-day-status portal-day-status-confirmed' };
    }
    return { text: range, cls: 'portal-day-status' };
  }
  if (d.mode === 'ON_CALL') {
    return { text: 'On call', cls: 'portal-day-status portal-day-status-oncall' };
  }
  if (d.mode === 'UNAVAILABLE') {
    return { text: 'Unavailable', cls: 'portal-day-status portal-day-status-unavailable' };
  }
  return { text: MODE_LABELS[d.mode] ?? 'Declared', cls: 'portal-day-status' };
}

function computeWeekDays(weekOffset: number): Date[] {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const day = today.getDay();
  const mondayShift = day === 0 ? -6 : 1 - day;
  const monday = new Date(today);
  monday.setDate(today.getDate() + mondayShift + weekOffset * 7);
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(monday);
    d.setDate(monday.getDate() + i);
    return d;
  });
}

function formatWeekRange(days: Date[]): string {
  const first = days[0];
  const last = days[days.length - 1];
  const sameMonth = first.getMonth() === last.getMonth();
  const monthFmt: Intl.DateTimeFormatOptions = { month: 'short' };
  if (sameMonth) {
    return `${first.getDate()}–${last.getDate()} ${last.toLocaleDateString('en-AU', monthFmt)}`;
  }
  return `${first.getDate()} ${first.toLocaleDateString('en-AU', monthFmt)} – ${last.getDate()} ${last.toLocaleDateString('en-AU', monthFmt)}`;
}

function formatDayName(d: Date): string {
  return d.toLocaleDateString('en-AU', { weekday: 'short' });
}
function formatDayDate(d: Date): string {
  return d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short' });
}
function formatFullDateLabel(d: Date): string {
  return d.toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short' });
}
function isSameDay(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear()
      && a.getMonth() === b.getMonth()
      && a.getDate() === b.getDate();
}
function isoDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
}
